import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Camera, ExternalLink, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import type { VendorTrustDocLink, VendorTrustLinks } from '@deepguide-ai/types'
import { urlHost } from '@deepguide-ai/shared'
import { DOC_TYPE_LABELS } from '@/pages/vendors/doc-type-labels'
import { invalidateVendorDocumentQueries } from './useVendorDocumentUpload'

/**
 * RFC-231 §6 — "Where to get the documents". Renders the catalog's
 * hand-verified links for a vendor: a portal / request-access link with a
 * one-line how-to for gated reports, and a `Save snapshot` action for public
 * pages (stores the rendered page in the stash with source URL + time).
 *
 * Data only: nothing is fetched without the user's click. With no `vendorId`
 * (Add Vendor dialog, vendor not created yet) the list is read-only.
 */

const ACCESS_LABEL: Record<VendorTrustDocLink['access'], { label: string; status: 'green' | 'amber' }> = {
  public: { label: 'Public', status: 'green' },
  request: { label: 'Request access', status: 'amber' },
  portal: { label: 'Customer portal', status: 'amber' },
}

/**
 * The one `Save snapshot` implementation. Exposed as a hook so the card list
 * here and the merged review checklist can present it differently without
 * forking the mutation, toasts and cache invalidation.
 */
export function useVendorPageSnapshot(vendorId: string | undefined, vendorName?: string) {
  const queryClient = useQueryClient()
  const snapshotMutation = useMutation(
    trpc.vendorManagement.snapshotPublicPage.mutationOptions()
  )
  const [busyKey, setBusyKey] = useState<string | null>(null)

  const saveSnapshot = async (
    docKey: string,
    link: VendorTrustDocLink,
    onDone?: () => void
  ) => {
    if (!vendorId) return
    setBusyKey(docKey)
    try {
      const result = await (snapshotMutation as any).mutateAsync({
        vendorId,
        url: link.url,
        documentType: docKey,
        title: `${vendorName ?? 'Vendor'} ${DOC_TYPE_LABELS[docKey] ?? docKey}`,
      })
      toast.success(
        result?.evidenceRequestsSatisfied > 0
          ? `Snapshot saved · ${result.evidenceRequestsSatisfied} evidence request(s) received`
          : 'Snapshot saved to the vendor documents'
      )
      invalidateVendorDocumentQueries(queryClient, vendorId)
      onDone?.()
    } catch (err) {
      // Loud failure by design (RFC-231 §6.3): offer the link instead.
      toast.error(
        err instanceof Error
          ? `${err.message}. Open the link and upload the document instead`
          : 'Snapshot failed. Open the link and upload the document instead'
      )
    } finally {
      setBusyKey(null)
    }
  }

  return { saveSnapshot, busyKey }
}

export function VendorTrustLinksList({
  links,
  vendorId,
  vendorName,
  onlyDocKeys,
  readOnly,
  compact,
  onSnapshot,
}: {
  links: VendorTrustLinks | null | undefined
  /** When set, public links get a `Save snapshot` action that stores into this vendor's stash. */
  vendorId?: string
  vendorName?: string
  /** Restrict to these doc keys (e.g. the outstanding evidence requests). */
  onlyDocKeys?: string[]
  readOnly?: boolean
  compact?: boolean
  onSnapshot?: () => void
}) {
  const { saveSnapshot, busyKey } = useVendorPageSnapshot(vendorId, vendorName)

  if (!links) return null
  const entries = Object.entries(links.docs ?? {}).filter(
    ([key]) => !onlyDocKeys || onlyDocKeys.includes(key)
  )
  if (entries.length === 0 && !links.trustCenterUrl) return null

  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      {links.trustCenterUrl && !onlyDocKeys && (
        <a
          href={links.trustCenterUrl}
          target='_blank'
          rel='noreferrer'
          className='font-body inline-flex items-center gap-1 text-[11px] text-cobalt-strong hover:underline'
        >
          Trust center · {urlHost(links.trustCenterUrl)}
          <ExternalLink className='h-3 w-3' />
        </a>
      )}
      {entries.map(([docKey, link]) => {
        const access = ACCESS_LABEL[link.access]
        const canSnapshot = link.access === 'public' && !!vendorId && !readOnly
        return (
          <div
            key={docKey}
            className='flex items-start justify-between gap-3 rounded-sm border border-border bg-card px-2.5 py-1.5'
          >
            <div className='min-w-0'>
              <div className='flex flex-wrap items-center gap-1.5'>
                <span className='font-body text-text-secondary text-[11px] font-medium'>
                  {DOC_TYPE_LABELS[docKey] ?? docKey}
                </span>
                <Badge status={access.status} className='h-4 px-1.5 text-[9px] uppercase'>
                  {access.label}
                </Badge>
              </div>
              <a
                href={link.url}
                target='_blank'
                rel='noreferrer'
                className='font-body inline-flex max-w-full items-center gap-1 truncate text-[10.5px] text-cobalt-strong hover:underline'
              >
                {link.url.replace(/^https?:\/\//, '')}
                <ExternalLink className='h-2.5 w-2.5 shrink-0' />
              </a>
              {link.how && (
                <p className='font-body text-text-subtle text-[10.5px]'>{link.how}</p>
              )}
            </div>
            {canSnapshot && (
              <Button
                size='sm'
                variant='outline'
                className='h-7 shrink-0 rounded-sm text-[11px]'
                disabled={busyKey !== null}
                onClick={() => void saveSnapshot(docKey, link, onSnapshot)}
                tooltip='Store a copy of this public page in the vendor documents, with source URL and time.'
              >
                {busyKey === docKey ? (
                  <Loader2 className='h-3 w-3 animate-spin' />
                ) : (
                  <Camera className='h-3 w-3' />
                )}
                Save snapshot
              </Button>
            )}
          </div>
        )
      })}
    </div>
  )
}
